import { ResolutionString } from 'src/lib/charting_library/charting_library';
import { PropertyKeyPairType } from 'src/setup/theme/style';
import { DepthType, KlinePriceAccuracyType } from 'src/types';
import { Option } from 'src/types/option';

export const SOCKET_SUB_TYPE = {
    TRADE_DATA: 'tradeData',
    USER: 'user',
};

export const SOCKET_TOPICS = {
    SLOW_BROKER: 'slowBroker',
    REAL_TIMES: 'realtimes',
    BROKER: 'broker',
    KLINE: 'kline',
    INDEX_KLINE: 'indexKline',
    MERGED_DEPTH: 'mergedDepth',
    TRADE: 'trade',
    FUTURES_ORDER: 'futures_order',
    FUTURES_POSITION: 'futures_position',
    FUTURES_TRADEABLE: 'futures_tradeable',
    ORDER: 'order',
    PLAN_ORDER: 'plan_order',
    MATCH: 'match',
    BALANCE: 'balance',
};

// Depth precision value, "0" is different from K-line price precision 0
export const DEPTH: DepthType = {
    '100000000': -8,
    '10000000': -7,
    '1000000': -6,
    '100000': -5,
    '10000': -4,
    '1000': -3,
    '100': -2,
    '10': -1,
    '1': 0,
    '0': 0,
    '0.1': 1,
    '0.01': 2,
    '0.001': 3,
    '0.0001': 4,
    '0.00001': 5,
    '0.000001': 6,
    '0.0000001': 7,
    '0.00000001': 8,
    '0.000000001': 9,
    '0.0000000001': 10,
    '0.00000000001': 11,
    '0.000000000001': 12,
    '0.000000000000000001': 18,
};

export const KLINE_PRICE_ACCURACY: KlinePriceAccuracyType = {
    0: 1,
    1: 10,
    2: 100,
    3: 1000,
    4: 10000,
    5: 100000,
    6: 1000000,
    7: 10000000,
    8: 100000000,
    9: 1000000000,
    10: 10000000000,
    11: 100000000000,
    12: 1000000000000,
};

export const REGEX = {
    FUTURE_SYMBOL_ID: /^\w+-(SWAP-USDT|SWAP-UDC)$/,
    DECIMAL_NUMBERS: /^[0-9]*(\.[0-9]*)?$/,
    ALPHA_START: /^[^a-zA-Z]+/,
    PERPETUAL_CONTRACT_POSTFIX: /-P$/,
    POSITIVE_DECIMAL: /^([1-9]\d*|0)(\.\d*)?$/,
};

export const INPUT_TYPES = {
    TEXT: 'text',
    EMAIL: 'email',
    NUMBER: 'number',
    CHECKBOX: 'checkbox',
};

export const ORDER_SIDE = {
    OPEN: 'OPEN',
    CLOSE: 'CLOSE',
};

export const ORDER_TYPE = {
    LIMIT: 'LIMIT',
    MARKET: 'MARKET',
    CONDITIONAL: 'STOP',
};

export const PRICE_TYPE = {
    LIMIT: 'INPUT',
    MARKET: 'MARKET_PRICE',
};

export const TIME_IN_FORCE = {
    GTC: 'GTC',
    IOC: 'IOC',
    FOK: 'FOK',
    MAKER: 'MAKER',
};

export const POST_ONLY_TIME = 'MAKER';

export const ORDER_BY = {
    QUANTITY: 'quantity',
    COST: 'cost',
};

export const CREATE_ORDER_SIDE = {
    BUY: 'BUY',
    SELL: 'SELL',
};

export const FavoriteCoinType = {
    SPOT: 1,
    FUTURE: 4,
};

export const TRADE_TYPE = {
    BUY_OPEN: 'BUY_OPEN',
    SELL_OPEN: 'SELL_OPEN',
    BUY_CLOSE: 'BUY_CLOSE',
    SELL_CLOSE: 'SELL_CLOSE',
};

interface LangaugeKeyMap {
    [key: string]: string;
}

export const TRADE_TYPE_LANG_MAP: LangaugeKeyMap = {
    BUY_OPEN: 'open_long',
    SELL_OPEN: 'open_short',
    BUY_CLOSE: 'close_short',
    SELL_CLOSE: 'close_long',
};

export const ORDER_STATUS = {
    ORDER_NEW: 'ORDER_NEW',
    NEW: 'NEW',
    FILLED: 'FILLED',
    ORDER_FILLED: 'ORDER_FILLED',
    ORDER_REJECTED: 'ORDER_REJECTED',
    REJECTED: 'REJECTED',
    CANCELED: 'CANCELED',
    ORDER_CANCELED: 'ORDER_CANCELED',
    PARTIALLY_FILLED: 'PARTIALLY_FILLED',
};

export const ORDER_STATUS_LANG_MAP: LangaugeKeyMap = {
    ORDER_NEW: 'untriggered',
    NEW: 'active',
    FILLED: 'filled',
    ORDER_FILLED: 'filled',
    ORDER_REJECTED: 'rejected',
    REJECTED: 'rejected',
    CANCELED: 'canceled',
    ORDER_CANCELED: 'canceled',
    PARTIALLY_FILLED: 'partially_filled',
};

export const POSITION_SIDE = {
    LONG: '1',
    SHORT: '0',
};

export const WALLET_ACCOUNT_TYPES = {
    GENERAL_ACCOUNT: 1,
    PERPETUALS_ACCOUNT: 3,
    MARGIN_ACCOUNT: 27,
};

export const DEFAULT_ACCOUNT_INDEX = 0;

export const CALCULATOR_TYPE = {
    ROR: 'rate_of_return',
    LIQ: 'est_liquidation_price',
};

export const SLIPPAGE = 0.0005;

export const InitialMargin = 8;

export const NOTIFICATION_TYPES = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARN: 'warn',
    INFO: 'info',
};

export const MAX_INPUT = 9999999999;

export const MENU_TYPE = {
    ASSETS: 'assets',
    ORDERS: 'orders',
    MY_ACCOUNT: 'my_account',
};

export const ORDER_TABLE_TABS = {
    POSITIONS: 'positions',
    CLOSED_PNL: 'closed_pl',
    ACTIVE_ORDERS: 'active_orders',
    CONDITIONAL_ORDERS: 'conditional_orders',
    FILLED: 'filled',
    ORDER_HISTORY: 'order_history',
    TRADE_HISTORY: 'trade_history',
};

export const POSITION_MARGIN_TABS = {
    INCREASE_MARGIN: 'increase_margin',
    DECREASE_MARGIN: 'decrease_margin',
};

export const POSITION_MARGIN_TYPES = {
    INCREASE: 'INC',
    DECREASE: 'DEC',
};

export const PRICE_INCREMENT_DECREMENT_VALUE = 0.5;

export const TRADING_TYPE = {
    DERIVATIVES: 'derivatives',
    SPOT: 'spot',
};

export const PAGE = {
    CONTRACT: 'contract',
    SPOT: 'spot',
};

export const ACCOUNT_TYPES = {
    CURRENCY_ACCOUNT: '0',
};

export const SPOT_ORDER_STATUS_LANG_MAP: LangaugeKeyMap = {
    ORDER_NEW: 'status_order_created',
    NEW: 'status_order_created',
    ORDER_FILLED: 'status_order_filled',
    FILLED: 'status_order_filled',
    ORDER_REJECTED: 'status_order_rejected',
    ORDER_CANCELED: 'status_order_canceled',
    PARTIALLY_FILLED: 'status_partially_filled',
    CANCELED: 'status_order_canceled',
};

export const SPOT_CONDITIONAL_ORDER_STATUS_LANG_MAP: LangaugeKeyMap = {
    ORDER_NEW: 'order_status_untriggered',
    NEW: 'order_status_untriggered',
};

export const MOBILE_TRADE_TABS: PropertyKeyPairType = {
    CHART: 'chart',
    DEPTH: 'depth',
    ORDER_BOOK: 'order_book',
    RECENT_TRADES: 'recent_trades',
};

export const LEVERAGE = {
    LONG: 'long',
    SHORT: 'short',
};

export const TRADING_VIEW_URL = 'https://www.tradingview.com/x/';

export const DATAFEED_SYMBOL_TYPE = {
    PERPETUAL: 'PERPETUAL',
    SWAP: 'SWAP',
};

export const POSITION_CLOSE_TYPE = {
    CURRENT_POSITION: 0, // Only close the current position that can be closed
    ALL_POSITION: 1, // Close all positions
};

export const HEADER_SETTINGS_DEFAULTS = {
    favoriteChartTypes: ['Line'],
    activeChartType: 1,
    favoriteIntervals: ['5', '15', '60', '240', '1D', '1W'] as ResolutionString[],
    chartInterval: '15',
};

export const SharePostLinksEnum = {
    FACEBOOK: 'https://www.facebook.com/sharer/sharer.php?',
    TWITTER: 'https://twitter.com/intent/tweet?',
    WHATSAPP: 'https://api.whatsapp.com/send?',
    TELEGRAM: 'https://t.me/share/url?',
    LINKEDIN: 'https://www.linkedin.com/sharing/share-offsite/?',
};

export const TokenLabel = {
    HOT: 'Hot',
    NEW: 'New',
};

export const languagesDropdownOptions: Option[] = [
    { label: 'English', value: 'en-us' },
    { label: 'Deutsch', value: 'de' },
    { label: 'Türkçe', value: 'tr' },
    { label: 'Русский', value: 'ru' },
    { label: 'Español', value: 'es' },
    { label: '한국인', value: 'ko-kr' },
    { label: 'Tiếng Việt', value: 'vi' },
    { label: 'Italiano', value: 'it' },
    { label: 'Português', value: 'pt' },
    { label: 'Lietuvių', value: 'lt' },
];

export const currenciesDropdownOptions: Option[] = [
    { label: 'USD', value: 'usd' },
    /*  { label: 'EUR', value: 'eur' },
    { label: 'RUB', value: 'rub' },
    { label: 'KRW', value: 'krw' },
    { label: 'TRY', value: 'try' },
    { label: 'VND', value: 'vnd' },*/
];

export const NotificationStatus = {
    READ: 'READ',
    UNREAD: 'UNREAD',
};

export const NotificationBusinessType = {
    PROMOTION: 'PROMOTION',
};

export const NotificationNoticeType = {
    POPUP: 'POP_UP',
    WEB: 'WEB',
};

export const KLINE_MAX_LIMIT = 2000;

export const HEADER_HEIGHT = '56px';
export const FOOTER_HEIGHT = '48px';

export enum NotificationStatusEnum {
    READ = 'READ',
    UNREAD = 'UNREAD',
}

export enum UserVerificationStatusEnum {
    NOT_VERIFIED = 0,
    UNDER_REVIEW = 1,
    VERIFIED = 2,
    REFUSED = 3,
}

export enum BonusStatusEnum {
    NEW = 0,
    ACTIVE = 1,
    EXPIRED = 2,
    USED = 3,
    CANCELED = 4,
}

export enum BonusTypeEnum {
    DEPOSIT = 0,
    AFFILIATE = 1,
    FUTURES_ACCOUNT = 2,
}
